import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@ofself/shell';
import { Verb } from '../../components/nucleus/NucleusPage';
import { ordered, useRunAction } from '../../components/nucleus/actions';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { BandHead } from '../../components/nucleus/Inspector';
import { LogOut } from 'lucide-react';
import { DETECTED_TZ, KNOWN_ZONES } from './selfData';

// SELF IS MOSTLY FORMS. A form page's body is one centred column of bands on
// the desk (the inspector's nx-band, wider). Each band is one setting or one
// group of them; what it can do sits at its foot, in the one action shape
// ({ id, label, run, primary?, danger?, disabled?, hint?, confirm? }), run
// through the one confirm dialog:
//
//   [ danger… ]                         [ others… ] [ primary (Save) ]
//
// The band's primary act — Save, for a form — always stands at the far end, so
// every Self form is finished in the same place. Switches and selects apply as
// they change; only typed fields wait for Save.

export function FormColumn({ children, wide = false }) {
    return <div className={`self-column${wide ? ' is-wide' : ''}`}>{children}</div>;
}

export function FormBand({ title, aside, lead, acts, children, id }) {
    return (
        <section className="nx-band self-band" id={id}>
            <BandHead title={title} aside={aside} />
            {lead && <p className="nx-note self-band__lead">{lead}</p>}
            {children && <div className="nx-band__body">{children}</div>}
            {acts && <BandActs acts={acts} />}
        </section>
    );
}

// A band's foot: its acts, danger to the left, the primary at the far end.
export function BandActs({ acts }) {
    const run = useRunAction();
    const [busy, setBusy] = useState(null);
    const list = ordered(acts);
    if (!list.length) return null;
    const danger = list.filter((a) => a.danger);
    const rest = list.filter((a) => !a.danger && !a.primary);
    const primary = list.filter((a) => a.primary && !a.danger);
    const press = async (a) => {
        setBusy(a.id);
        try { await run(a); } finally { setBusy(null); }
    };
    const button = (a) => (
        <FootButton
            key={a.id}
            primary={!!a.primary && !a.danger}
            danger={!!a.danger}
            disabled={!!a.disabled}
            busy={busy === a.id}
            title={a.hint || undefined}
            onClick={() => press(a)}
        >
            {a.label}
        </FootButton>
    );
    return (
        <SheetFoot left={danger.length > 0 && danger.map(button)}>
            {rest.map(button)}
            {primary.map(button)}
        </SheetFoot>
    );
}

// A page verb in the corner that is an action (so it asks first through the
// one confirm, and a failure is a toast).
export function ActVerb({ act }) {
    const run = useRunAction();
    if (!act || act.hidden) return null;
    return (
        <Verb icon={act.icon || <LogOut />} primary={!!act.primary} danger={!!act.danger} disabled={!!act.disabled} title={act.hint} onClick={() => run(act)}>
            {act.label}
        </Verb>
    );
}

// A typed confirmation inside the one confirm dialog: the body carries a field,
// and the act refuses (a toast) unless the word was typed.
export function typedConfirm(word, body) {
    const typed = { value: '' };
    return {
        body: (
            <div className="self-typed">
                <p className="nx-note">{body}</p>
                <TextField
                    id={`self-typed-${word.replace(/\W+/g, '-').toLowerCase()}`}
                    label={`Type ${word} to confirm`}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) => { typed.value = e.target.value; }}
                />
            </div>
        ),
        check: () => {
            if (typed.value.trim() !== word) throw new Error(`Nothing was deleted: type ${word} to confirm.`);
        },
    };
}

// A value on its own line with a Copy beside it (credentials, codes).
export function CopyLine({ label, value }) {
    const [done, setDone] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setDone(true);
            setTimeout(() => setDone(false), 1400);
        } catch { /* the value is on screen to select by hand */ }
    };
    return (
        <div className="self-copy">
            {label && <span className="self-copy__label">{label}</span>}
            <code className="self-copy__value">{value}</code>
            <FootButton onClick={copy}>{done ? 'Copied' : 'Copy'}</FootButton>
        </div>
    );
}

// A TIMEZONE, FOUND BY NAME. Four hundred IANA ids in a native select is a wall;
// this reads them as places ("New York", "America · GMT-4"), finds one by any
// word of it or its offset, and offers this device's zone first.
const offsetOf = (zone, at) => {
    try {
        return new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'shortOffset' })
            .formatToParts(at).find((p) => p.type === 'timeZoneName')?.value || '';
    } catch { return ''; }
};
export function zonePlace(zone) {
    const parts = String(zone || '').split('/');
    return {
        city: (parts[parts.length - 1] || '').replace(/_/g, ' '),
        region: parts.slice(0, -1).join(' · ').replace(/_/g, ' '),
    };
}

export function ZoneField({ label = 'Timezone', value, onChange }) {
    const zones = useMemo(() => {
        const now = new Date();
        const list = value && !KNOWN_ZONES.includes(value) ? [value, ...KNOWN_ZONES] : KNOWN_ZONES;
        return list.map((z) => ({ z, ...zonePlace(z), offset: offsetOf(z, now) }));
    }, [value]);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const box = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);
    const q = query.trim().toLowerCase();
    const matches = q
        ? zones.filter((o) => `${o.city} ${o.region} ${o.z} ${o.offset}`.toLowerCase().includes(q)).slice(0, 8)
        : zones.filter((o) => o.z === DETECTED_TZ && o.z !== value);
    const current = zones.find((o) => o.z === value);
    const shown = current ? `${current.city} · ${current.region ? `${current.region} · ` : ''}${current.offset}` : '';
    const pick = (z) => { onChange(z); setQuery(''); setOpen(false); setCursor(0); };
    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((c) => Math.min(c + 1, matches.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
        else if (e.key === 'Enter' && open && matches[cursor]) { e.preventDefault(); pick(matches[cursor].z); }
        else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };
    return (
        <div ref={box} className="self-token">
            <span className="self-select__label">{label}</span>
            <div className="self-token__field">
                <input value={open ? query : shown} aria-label={label}
                    placeholder={open ? (shown || 'Search a city or region') : 'Not set (read as UTC)'}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0); }}
                    onFocus={() => { setOpen(true); setQuery(''); }} onKeyDown={onKeyDown} />
            </div>
            {open && matches.length > 0 && (
                <div className="self-token__menu" role="listbox">
                    {matches.map((o, i) => (
                        <div key={o.z} role="option" aria-selected={i === cursor} className={`self-token__opt${i === cursor ? ' is-on' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); pick(o.z); }} onMouseEnter={() => setCursor(i)}>
                            <span>{!q && o.z === DETECTED_TZ ? `This device: ${o.city}` : o.city}</span>
                            <span className="self-token__n">{[o.region, o.offset].filter(Boolean).join(' · ')}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
